// Note: strongPasswordSchema import removed - using explicit regex validation instead
// See JSDoc below for rationale

/**
 * Password validation requirements checker
 *
 * **Implementation Approach:**
 * Uses explicit regex validation instead of parsing Zod error messages for robustness.
 * String matching against error messages (e.g., `msg.includes('uppercase')`) is fragile
 * and can break silently if Zod changes error message wording in a patch release.
 *
 * **Why both Zod and regex?**
 * - Zod schema (strongPasswordSchema) is source of truth for form validation
 * - Explicit regex checks ensure UI indicators remain accurate even if Zod messages change
 * - Regex patterns match exactly what strongPasswordSchema uses (see authSchemas.ts:20-28)
 *
 * **Regex Patterns (must match strongPasswordSchema):**
 * - Min length: `pwd.length >= 8`
 * - Uppercase: `/[A-Z]/`
 * - Lowercase: `/[a-z]/`
 * - Number: `/[0-9]/`
 * - Symbol: `/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/`
 */
export interface PasswordRequirement {
  label: string;
  isMet: boolean;
}

export function checkPasswordRequirements(password: string | undefined): PasswordRequirement[] {
  const pwd = password || '';

  // Explicit regex validation for each requirement (matches strongPasswordSchema)
  const hasMinLength = pwd.length >= 8;
  const hasUppercase = /[A-Z]/.test(pwd);
  const hasLowercase = /[a-z]/.test(pwd);
  const hasNumber = /[0-9]/.test(pwd);
  const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(pwd);

  return [
    {label: 'At least 8 characters long', isMet: hasMinLength},
    {label: 'At least 1 uppercase letter', isMet: hasUppercase},
    {label: 'At least 1 lowercase letter', isMet: hasLowercase},
    {label: 'At least 1 number', isMet: hasNumber},
    {label: 'At least 1 symbol', isMet: hasSymbol},
  ];
}
